import { useState, useEffect, useRef } from 'react';
import styles from './WeatherWidget.module.css';

// ─── 12 Jordanian Governorates ───────────────────────────────────────────────
const GOVERNORATES = [
  { id: 'amman',   name: 'Amman',   nameAr: 'عمّان',   lat: 31.9522, lon: 35.9330 },
  { id: 'zarqa',   name: 'Zarqa',   nameAr: 'الزرقاء', lat: 32.0728, lon: 36.0879 },
  { id: 'irbid',   name: 'Irbid',   nameAr: 'إربد',    lat: 32.5563, lon: 35.8503 },
  { id: 'balqa',   name: 'Balqa',   nameAr: 'البلقاء', lat: 32.0364, lon: 35.7289 },
  { id: 'madaba',  name: 'Madaba',  nameAr: 'مادبا',   lat: 31.7161, lon: 35.7937 },
  { id: 'karak',   name: 'Karak',   nameAr: 'الكرك',   lat: 31.1831, lon: 35.7027 },
  { id: 'tafilah', name: 'Tafilah', nameAr: 'الطفيلة', lat: 30.8371, lon: 35.6042 },
  { id: 'maan',    name: "Ma'an",   nameAr: 'معان',    lat: 30.1989, lon: 35.7337 },
  { id: 'aqaba',   name: 'Aqaba',   nameAr: 'العقبة',  lat: 29.5321, lon: 35.0063 },
  { id: 'jerash',  name: 'Jerash',  nameAr: 'جرش',     lat: 32.2763, lon: 35.8997 },
  { id: 'ajloun',  name: 'Ajloun',  nameAr: 'عجلون',   lat: 32.3325, lon: 35.7519 },
  { id: 'mafraq',  name: 'Mafraq',  nameAr: 'المفرق',  lat: 32.3428, lon: 36.2037 },
];

const WMO = {
  0:  { label: 'Clear Sky',       icon: '☀️' },
  1:  { label: 'Mainly Clear',    icon: '🌤️' },
  2:  { label: 'Partly Cloudy',   icon: '⛅' },
  3:  { label: 'Overcast',        icon: '☁️' },
  45: { label: 'Foggy',           icon: '🌫️' },
  48: { label: 'Icy Fog',         icon: '🌫️' },
  51: { label: 'Light Drizzle',   icon: '🌦️' },
  53: { label: 'Drizzle',         icon: '🌦️' },
  55: { label: 'Heavy Drizzle',   icon: '🌧️' },
  61: { label: 'Light Rain',      icon: '🌧️' },
  63: { label: 'Rain',            icon: '🌧️' },
  65: { label: 'Heavy Rain',      icon: '🌧️' },
  71: { label: 'Light Snow',      icon: '🌨️' },
  73: { label: 'Snow',            icon: '❄️' },
  75: { label: 'Heavy Snow',      icon: '❄️' },
  80: { label: 'Rain Showers',    icon: '🌦️' },
  95: { label: 'Thunderstorm',    icon: '⛈️' },
};

function buildForecast(data) {
  const { daily } = data;
  return daily.time.map((dateStr, i) => {
    const date = new Date(dateStr + 'T00:00:00');
    const dayName = i === 0
      ? 'Today'
      : date.toLocaleDateString('en', { weekday: 'short' });
    const wmo = WMO[daily.weather_code[i]] ?? { label: 'Unknown', icon: '🌡️' };
    return {
      dayName,
      icon:    wmo.icon,
      label:   wmo.label,
      tempMax: Math.round(daily.temperature_2m_max[i]),
      tempMin: Math.round(daily.temperature_2m_min[i]),
    };
  });
}

export default function WeatherWidget() {
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(false);
  const [selected, setSelected] = useState(GOVERNORATES[0]);
  const [open, setOpen]         = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setForecast([]);

    const url =
      `https://api.open-meteo.com/v1/forecast` +
      `?latitude=${selected.lat}&longitude=${selected.lon}` +
      `&daily=weather_code,temperature_2m_max,temperature_2m_min` +
      `&forecast_days=7&timezone=auto`;

    fetch(url)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { if (!cancelled) { setForecast(buildForecast(d)); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });

    return () => { cancelled = true; };
  }, [selected]);

  const today = forecast[0];

  return (
    <div className={styles.widget}>

      {/* Top bar: city dropdown + powered by */}
      <div className={styles.topBar}>
        <div className={styles.dropWrap} ref={dropRef}>
          <button
            className={`${styles.dropBtn} ${open ? styles.dropBtnOpen : ''}`}
            onClick={() => setOpen(o => !o)}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span>📍</span>
            <span className={styles.dropName}>{selected.name}</span>
            <svg className={`${styles.dropArrow} ${open ? styles.dropArrowUp : ''}`} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {open && (
            <ul className={styles.dropList} role="listbox">
              {GOVERNORATES.map(gov => (
                <li
                  key={gov.id}
                  role="option"
                  aria-selected={gov.id === selected.id}
                  className={`${styles.dropItem} ${gov.id === selected.id ? styles.dropItemActive : ''}`}
                  onClick={() => { setSelected(gov); setOpen(false); }}
                >
                  <span className={styles.dropItemName}>{gov.name}</span>
                  <span className={styles.dropItemAr}>{gov.nameAr}</span>
                  {gov.id === selected.id && <span className={styles.dropCheck}>✓</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
        <a className={styles.poweredBy} href="https://open-meteo.com/" target="_blank" rel="noreferrer">Open-Meteo</a>
      </div>

      {/* Today: icon + temp + label — compact inline row */}
      {loading && <div className={styles.loadingHero} />}
      {!loading && !error && today && (
        <div className={styles.todayBlock}>
          <span className={styles.todayIcon}>{today.icon}</span>
          <span className={styles.todayTemp}>{today.tempMax}°C</span>
          <span className={styles.todayLabel}>{today.label}</span>
        </div>
      )}

      {/* 7-day forecast row */}
      {!loading && !error && (
        <div className={styles.forecastRow}>
          {forecast.map((day, i) => (
            <div key={i} className={styles.forecastCol}>
              <div className={styles.fDay}>{day.dayName}</div>
              <div className={styles.fIcon}>{day.icon}</div>
              <div className={styles.fMax}>{day.tempMax}°</div>
              <div className={styles.fMin}>{day.tempMin}°</div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
